import React, { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { useMaterialUIController } from "../../context";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useSelector } from "react-redux";
import {
  AddSlideCall,
  AddSubTitlesCall,
  AddTitlesCall,
  DeleteSubTitlesCall,
  DeleteTitlesCall,
  EditSubTitlesCall,
  EditTitlesCall,
  GetTitlesCall,
  SwapTitlesCall,
  EditTitleImageCall,
} from "../../redux/api/title.api";
import Icon from "@mui/material/Icon";
import { isEmpty } from "lodash";
import { useNavigate } from "react-router-dom";
import MDSnackbar from "components/MDSnackbar";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useDispatch } from "react-redux";
import { store } from "../../redux/store";
import axios from "axios";
import { basicURL } from "../../apiManager/APIConfig";
import "@google/model-viewer";

// Normalize 3D model URL like ShowThreeD (relative -> absolute with basicURL)
function getNormalizedModelUrl(slide) {
  const files = slide?.Files ?? slide?.files ?? [];
  const first = Array.isArray(files) ? files[0] : null;
  let url = first?.url ?? first?.path ?? null;
  if (url == null || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
}

function Titles({ type, readOnly = false, library_category = null, embedded = false, addFromLibraryNode = null, itemsFromLibrary = null, onRemoveLibraryRef = null, onLibraryRefresh = null }) {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const slideType = type;
  const three_d_model = type === "three_d_model" || library_category === "three_d_model";
  const initialItems = useSelector((state) => state?.title?.titles);
  const lang = useSelector((state) => state.general?.lang);
  const [fileRefs, setFileRefs] = useState({}); // store refs by subtitle ID

  // Defensive cleanup: strip stray ")}" text nodes that sometimes appear
  // between slide cards due to legacy content. This watcher keeps the DOM tidy
  // without affecting React state.
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return;
    }

    const sanitizeNode = (node) => {
      if (!node || node.nodeType !== Node.TEXT_NODE) return;
      const value = node.nodeValue;
      if (value && value.includes(")}")) {
        node.nodeValue = value.replace(/\)\}/g, "");
      }
    };

    // Initial pass
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let n = walker.nextNode();
    while (n) {
      sanitizeNode(n);
      n = walker.nextNode();
    }

    // Observe future changes
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "characterData") {
          sanitizeNode(m.target);
        }
        if (m.type === "childList") {
          m.addedNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
              sanitizeNode(child);
            } else if (child.nodeType === Node.ELEMENT_NODE) {
              const walkerInner = document.createTreeWalker(child, NodeFilter.SHOW_TEXT);
              let t = walkerInner.nextNode();
              while (t) {
                sanitizeNode(t);
                t = walkerInner.nextNode();
              }
            }
          });
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const rawItems = Array.isArray(initialItems) ? initialItems : [];
  // Treat missing library_category as "his" so existing private titles still show (e.g. in Histology tab)
  const filteredByCategory = library_category
    ? rawItems.filter((i) => (i.library_category || "his") === library_category)
    : rawItems;
  const [items, setItems] = useState(filteredByCategory);
  const displayItems = (embedded && itemsFromLibrary != null)
    ? (library_category ? itemsFromLibrary.filter((i) => (i.library_category || "his") === library_category) : itemsFromLibrary)
    : items;
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [message, setMessage] = useState("");
  // const [lang, setLanguage] = useState("en");

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const fetchTitles = async () => {
    await GetTitlesCall(lang, type);
  };

  useEffect(() => {
    setItems([]);
    fetchTitles();
  }, [type, lang]);

  useEffect(() => {
    const raw = Array.isArray(initialItems) ? initialItems : [];
    const filtered = library_category ? raw.filter((i) => (i.library_category || "his") === library_category) : raw;
    setItems(filtered);
  }, [initialItems, library_category]);

  const handleAddTitle = () => {
    AddTitlesCall({
      title: `New Title ${(displayItems || items)?.length + 1}`,
      body: `Description ${(displayItems || items)?.length + 1}`,
      lang,
      type,
      ...(library_category && { library_category }),
    }).then(() => { if (embedded && onLibraryRefresh) onLibraryRefresh(); });
  };

  const handleAddSubtitle = (itemId) => {
    AddSubTitlesCall({
      title: `New Subtitle ${(displayItems || items)?.length + 1}`,
      body: `Description ${(displayItems || items)?.length + 1}`,
      title_id: itemId,
      lang,
      type,
    }).then(() => { if (embedded && onLibraryRefresh) onLibraryRefresh(); });
  };

  const handleEdit = async (id, newValue, type) => {
    if (type === "MainTitle" || type === "MainBody") {
      const body = {
        id,
        lang,
        type: slideType,
        [type === "MainTitle" ? "title" : "body"]: newValue,
      };
      await EditTitlesCall(body);
    } else {
      const body = {
        id,
        lang,
        type: slideType,
        [type === "SubTitle" ? "title" : "body"]: newValue,
      };
      await EditSubTitlesCall(body);
    }

    if (embedded && typeof onLibraryRefresh === "function") {
      await onLibraryRefresh();
    }
  };

  const handleDeleteSubTitle = async (id) => {
    const body = {
      id,
      lang,
      type,
    };
    const confirmation = window.confirm(
      "Are you sure you want to delete this subtitle? All your pins, texts and slide inside this subtitle will be deleted permanently."
    );
    if (confirmation) {
      const res = await DeleteSubTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage("The subtitle deleted successfully!");
        setTimeout(() => {
          openSuccessSB();
        }, 200);
        if (embedded && typeof onLibraryRefresh === "function") {
          onLibraryRefresh();
        }
      }
    }
  };
  const handleDeleteTitle = async (id) => {
    const body = {
      id,
      lang,
      type,
    };
    const confirmation = window.confirm(
      "Are you sure you want to delete this title and all its subtitles? All your pins, texts and slide inside this subtitle will be deleted permanently."
    );
    if (confirmation) {
      const res = await DeleteTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage("The title deleted successfully!");
        setTimeout(() => {
          openSuccessSB();
        }, 200);
        if (embedded && typeof onLibraryRefresh === "function") {
          onLibraryRefresh();
        }
      }
    }
  };

  const handleAddMaterial = async (id) => {
    const res = await AddSlideCall({ title_id: id, lang, type });
    if (!res?.slide?.id) return;
    const base = three_d_model
      ? `/showThreeD/${res.slide.id}/${lang}`
      : `/showTitle/${res.slide.id}/${lang}`;
    const url = embedded ? `${base}?private=1` : base;
    navigate(url);
  };

  const handleViewSlide = (subtitle) => {
    const primary =
      (subtitle?.slides && subtitle.slides[0]) ||
      (subtitle?.referencedSlides && subtitle.referencedSlides[0]) ||
      null;
    const slideId = primary?.id;
    if (!slideId) return;
    const base = three_d_model
      ? `/showThreeD/${slideId}/${lang}`
      : `/showTitle/${slideId}/${lang}`;
    const url = embedded ? `${base}?private=1` : base;
    navigate(url);
  };

  const handleViewSlideById = (slide) => {
    const slideId = slide?.id;
    if (!slideId) return;
    const base = three_d_model
      ? `/showThreeD/${slideId}/${lang}`
      : `/showTitle/${slideId}/${lang}`;
    const url = embedded ? `${base}?private=1` : base;
    navigate(url);
  };

  const getFileRef = (id) => {
    if (!fileRefs[id]) {
      fileRefs[id] = React.createRef(); // create ref if not exists
    }
    return fileRefs[id];
  };

  const handleAddThumbNail = async (event, id) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Handle `.zip` file upload
        const formdata = new FormData();
        formdata.append("file", file);
        formdata.append("entity_type", "public");
        formdata.append("entity_id", "3989785d-49d1-49da-9373-0ed41c1a76bd");
        formdata.append("destination", "public");
        formdata.append("folder_name", "public");

        const access_token = store.getState().auth?.access_token;
        let myHeaders = new Headers();
        myHeaders.append("Authorization", access_token);

        myHeaders = {
          Authorization: access_token,
        };

        const response = await axios.post(`${basicURL}/file/upload`, formdata, {
          headers: myHeaders,
          onUploadProgress: (progressEvent) => {
            // Calculate the progress percentage
            // const percent = Math.round(
            //   (progressEvent.loaded / progressEvent.total) * 100
            // );
          },
        });
        console.log("pippo response", response);
        const thumbnail = response?.data?.download_url;
        console.log("ZIP file uploaded:", thumbnail);
        const body = {
          id,
          lang,
          type: slideType,
          image: thumbnail,
        };
        console.log("ZIP file body:", body);

        EditTitleImageCall(body);

        // window.location.reload();
      } catch (error) {
        console.error("Upload error:", error);
      }
    }
  };

  const renderItem = (item, index, list = items) => {
    const filteredTitle = item?.titleLocalizations?.filter(
      (e) => e.field == "title"
    );
    const filteredBody = item?.titleLocalizations?.filter(
      (e) => e.field == "body"
    );

    const title = filteredTitle?.[0]?.value || "Click to edit the title";
    const body = filteredBody?.[0]?.value || "Click to edit the body";
    const gapPx = 15;
    return (
      <MDBox display="flex" flexDirection="column" alignItems="flex-start" sx={{ mb: 0, px: `${gapPx}px`, pt: 0, pb: 0.5 }}>
          {!readOnly && (
            <MDBox display="flex" gap={0.5} sx={{ mb: `${gapPx}px` }}>
              {index !== 0 && (
                <Tooltip title="Move up">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const body = {
                        current_id: item?.id,
                        goal_id: list?.[index - 1]?.id,
                        lang,
                        type,
                      };
                      SwapTitlesCall(body);
                    }}
                    sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                  >
                    <ArrowDropUpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {list?.length !== index + 1 && (
                <Tooltip title="Move down">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const body = {
                        current_id: item?.id,
                        goal_id: list?.[index + 1]?.id,
                        lang,
                        type,
                      };
                      SwapTitlesCall(body);
                    }}
                    sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                  >
                    <ArrowDropDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          )}

          <EditableTitle
            title={title}
            titleFont="title"
            onEdit={(newTitle) => handleEdit(item.id, newTitle, "MainTitle")}
            readOnly={readOnly}
          />
          {!readOnly && (
            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} width="100%" sx={{ mt: `${gapPx}px` }}>
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDButton
                  color="primary"
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={() => handleAddSubtitle(item.id)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 3,
                    px: 2,
                    boxShadow: "none",
                    "&:hover": { boxShadow: theme.shadows[2] },
                  }}
                >
                  {embedded ? "Add Slide" : "Add Subtitle"}
                </MDButton>
                {embedded && (typeof addFromLibraryNode === "function" ? addFromLibraryNode(item) : addFromLibraryNode)}
              </MDBox>
              <MDButton
                color="error"
                variant="outlined"
                onClick={() => handleDeleteTitle(item.id)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 3,
                  borderWidth: 1.5,
                  "&:hover": { borderWidth: 1.5, bgcolor: alpha(theme.palette.error.main, 0.06) },
                }}
              >
                Delete
              </MDButton>
            </MDBox>
          )}

          {item?.subtitles?.length > 0 && (
            <MDBox sx={{ mt: `${gapPx}px`, display: "flex", flexWrap: "wrap", gap: "20px" }}>
              {item.subtitles.map((subtitle, idx) => {
              const fileInputRef = getFileRef(subtitle.id);

              const ownSlides = subtitle?.slides || [];
              const refSlides = subtitle?.referencedSlides || [];
              const primarySlide = ownSlides[0] || refSlides[0] || null;
              const hasAnySlide = !!primarySlide;
              const label = hasAnySlide ? "View" : "Add Material";

              const filteredTitle = subtitle?.titleLocalizations?.filter(
                (e) => e.field == "title"
              );
              const filteredBody = subtitle?.titleLocalizations?.filter(
                (e) => e.field == "body"
              );

              const title =
                filteredTitle?.[0]?.value || "Click to edit the sub title";
              const body =
                filteredBody?.[0]?.value || "Click to edit the sub body";
              return (
                <React.Fragment key={subtitle.id}>
                <MDBox sx={{ width: 224, minWidth: 196, flex: "0 0 auto" }}>
                  <Card
                    variant="outlined"
                    sx={{
                      pt: 0,
                      px: 0,
                      pb: `${gapPx}px`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      borderRadius: 3,
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      color: "text.main",
                      boxShadow: theme.shadows[1],
                      overflow: "hidden",
                      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                      "&:hover": { boxShadow: theme.shadows[4], borderColor: theme.palette.divider },
                      "& > *:first-of-type": { margin: 0, padding: 0, width: "100%", maxWidth: "100%" },
                    }}
                  >
                    {(three_d_model && !subtitle?.image && primarySlide && getNormalizedModelUrl(primarySlide)) ? (
                      <MDBox
                        sx={{
                          width: "100%",
                          height: 126,
                          overflow: "hidden",
                          margin: 0,
                          padding: 0,
                          flexShrink: 0,
                          bgcolor: "background.elevated",
                        }}
                      >
                        <iframe
                          src={
                            typeof window !== "undefined" && primarySlide
                              ? `${window.location.origin}/model-viewer.html?src=${encodeURIComponent(getNormalizedModelUrl(primarySlide))}&theme=${darkMode ? "dark" : "light"}`
                              : ""
                          }
                          title={`3D model: ${title}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            display: "block",
                          }}
                        />
                      </MDBox>
                    ) : (() => {
                      const baseUrl = primarySlide?.Files?.[0]?.url;
                      const slideImageUrl = subtitle?.image ?? (isEmpty(baseUrl) ? null : `${baseUrl}/3/3/3.png`);
                      const isEmptySlide = slideImageUrl == null;
                      const isDark = theme.palette.mode === "dark";
                      return (
                        <div
                          className="titles-card-thumb"
                          style={{
                            position: "relative",
                            width: "100%",
                            height: 126,
                            overflow: "hidden",
                            margin: 0,
                            padding: 0,
                            lineHeight: 0,
                            flexShrink: 0,
                            background: isEmptySlide
                              ? (isDark
                                  ? "linear-gradient(160deg, rgba(35,38,42,0.99) 0%, rgba(22,24,28,0.99) 50%, rgba(18,20,24,0.99) 100%)"
                                  : "linear-gradient(160deg, rgba(250,251,253,0.99) 0%, rgba(240,242,246,0.99) 50%, rgba(232,235,241,0.99) 100%)")
                              : (theme.palette.background?.elevated || "#1a1a1a"),
                            direction: "ltr",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isEmptySlide ? (
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              gap={1.25}
                              sx={{
                                color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.42)",
                              }}
                            >
                              <MDBox
                                sx={{
                                  width: 72,
                                  height: 72,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                                  border: "1px solid",
                                  borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                  boxShadow: isDark
                                    ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                                    : "inset 0 1px 0 rgba(255,255,255,0.6)",
                                }}
                              >
                                <Icon
                                  sx={{
                                    fontSize: 44,
                                    opacity: 0.85,
                                  }}
                                >
                                  photo_library
                                </Icon>
                              </MDBox>
                              <MDTypography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  letterSpacing: "0.04em",
                                  textTransform: "uppercase",
                                  opacity: 0.95,
                                  fontSize: "0.7rem",
                                }}
                              >
                                Empty slide
                              </MDTypography>
                            </MDBox>
                          ) : (
                            <img
                              src={slideImageUrl}
                              alt={title}
                              draggable={false}
                              style={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                right: 0,
                                bottom: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center top",
                                margin: 0,
                                padding: 0,
                                display: "block",
                                boxSizing: "border-box",
                              }}
                            />
                          )}
                        </div>
                      );
                    })()}

                    <MDBox width="100%" display="flex" flexDirection="column" alignItems="flex-start" sx={{ minWidth: 0, px: `${gapPx}px` }}>
                      <EditableTitle
                        title={title}
                        titleFont="subtitle"
                        onEdit={(newTitle) =>
                          handleEdit(subtitle.id, newTitle, "SubTitle")
                        }
                        readOnly={readOnly}
                      />

                      {!readOnly && (
                        <MDBox display="flex" gap={0.5} sx={{ my: `${gapPx}px` }}>
                          {idx !== 0 && (
                            <Tooltip title="Move left">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const body = {
                                    current_id: subtitle?.id,
                                    goal_id: item?.subtitles?.[idx - 1]?.id,
                                    lang,
                                    type,
                                  };
                                  SwapTitlesCall(body);
                                }}
                                sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                              >
                                <ArrowLeftIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {item?.subtitles?.length !== idx + 1 && (
                            <Tooltip title="Move right">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const body = {
                                    current_id: subtitle?.id,
                                    goal_id: item?.subtitles?.[idx + 1]?.id,
                                    lang,
                                    type,
                                  };
                                  SwapTitlesCall(body);
                                }}
                                sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                              >
                                <ArrowRightIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </MDBox>
                      )}
                      <MDBox display="flex" width="100%" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: `${gapPx}px` }}>
                        {!readOnly && (
                          <>
                            <MDButton
                              color="error"
                              variant="outlined"
                              size="small"
                              onClick={() => handleDeleteSubTitle(subtitle.id)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 3,
                                px: 1.5,
                                borderWidth: 1.5,
                                "&:hover": { borderWidth: 1.5, bgcolor: alpha(theme.palette.error.main, 0.06) },
                              }}
                            >
                              Delete
                            </MDButton>
                            <input
                              type="file"
                              ref={fileInputRef}
                              style={{ display: "none" }}
                              onChange={(event) =>
                                handleAddThumbNail(event, subtitle.id)
                              }
                            />
                            <Tooltip title="Change thumbnail">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                  borderRadius: 2,
                                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                                }}
                              >
                                <Icon fontSize="small">image</Icon>
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <MDButton
                          color="primary"
                          variant="contained"
                          size="small"
                          onClick={() =>
                            readOnly || hasAnySlide
                              ? handleViewSlide(subtitle)
                              : handleAddMaterial(subtitle.id)
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 3,
                            px: 1.75,
                            boxShadow: "none",
                            "&:hover": { boxShadow: theme.shadows[2] },
                          }}
                        >
                          {readOnly ? "View" : label}
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
                )}
                {ownSlides.length > 0 && refSlides.map((refSlide) => {
                  const refThumbUrl = refSlide?.Files?.[0]?.url ? `${refSlide.Files[0].url}/3/3/3.png` : null;
                  const slideTitle = refSlide?.slide_title
                    || refSlide?.slideLocalizations?.find((l) => l.field === "title")?.value?.trim()
                    || refSlide?.slide_localizations?.find((l) => l.field === "title")?.value?.trim();
                  const refLabel = slideTitle || "Slide";
                  const isDark = theme.palette.mode === "dark";
                  return (
                    <MDBox key={refSlide.id} sx={{ width: 224, minWidth: 196, flex: "0 0 auto" }}>
                      <Card
                        variant="outlined"
                        sx={{
                          pt: 0,
                          px: 0,
                          pb: 0,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 3,
                          borderColor: "divider",
                          bgcolor: "background.paper",
                          boxShadow: theme.shadows[1],
                          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                          "&:hover": { boxShadow: theme.shadows[4], borderColor: alpha(theme.palette.primary.main, 0.35) },
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            height: 126,
                            overflow: "hidden",
                            flexShrink: 0,
                            background: refThumbUrl ? undefined : (isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.06)"),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {refThumbUrl ? (
                            <Box
                              component="img"
                              src={refThumbUrl}
                              alt={refLabel}
                              sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center top",
                              }}
                            />
                          ) : (
                            <Icon sx={{ fontSize: 36, color: "text.secondary" }}>photo_library</Icon>
                          )}
                        </Box>
                        <MDBox sx={{ px: `${gapPx}px`, py: 1.25 }}>
                          <MDTypography variant="body2" fontWeight={600} sx={{ display: "block", mb: 1.25, lineHeight: 1.3 }}>{refLabel}</MDTypography>
                          <MDBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                            <MDButton
                              color="primary"
                              variant="contained"
                              size="small"
                              onClick={() => handleViewSlideById(refSlide)}
                              startIcon={<Icon sx={{ fontSize: 18 }}>visibility</Icon>}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 3,
                                px: 1.5,
                                boxShadow: "none",
                                "&:hover": { boxShadow: theme.shadows[2] },
                              }}
                            >
                              View
                            </MDButton>
                            {embedded && onRemoveLibraryRef && refSlide.public_title_id && (
                              <Tooltip title="Remove from library">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => onRemoveLibraryRef(refSlide.public_title_id, refSlide.id, subtitle.id)}
                                  sx={{
                                    borderRadius: 2,
                                    bgcolor: alpha(theme.palette.error.main, 0.06),
                                    "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.12) },
                                  }}
                                >
                                  <Icon sx={{ fontSize: 20 }}>link_off</Icon>
                                </IconButton>
                              </Tooltip>
                            )}
                          </MDBox>
                        </MDBox>
                      </Card>
                    </MDBox>
                  );
                })}
                </React.Fragment>
              );
            })}
            </MDBox>
          )}
      </MDBox>
    );
  };

  const content = (
    <>
      <MDBox py={1} mb={1} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <MDBox display="flex" alignItems="center" gap={2}>
          {!readOnly && (
            <MDButton
              color="primary"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => handleAddTitle()}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Add Title
            </MDButton>
          )}
        </MDBox>
      </MDBox>
      <MDBox py={0} width="100%">
        <Grid container spacing={0.5}>
          {Array.isArray(displayItems) && displayItems.length > 0 &&
            displayItems.map((item, index) => (
              <Grid item xs={12} key={item.id}>
                {renderItem(item, index, displayItems)}
              </Grid>
            ))}
        </Grid>
      </MDBox>
      <MDSnackbar
        color="success"
        icon="check"
        title={message}
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title={message}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
      {!embedded && <Footer />}
    </>
  );

  if (embedded) return content;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
}

const capitalizeFirst = (str) =>
  str && typeof str === "string" && str.length
    ? str.charAt(0).toUpperCase() + str.slice(1)
    : str;

const EditableTitle = ({ title, onEdit, titleFont, readOnly = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(title);
  const displayTitle = capitalizeFirst(title);
  let fontSize = "1rem";
  let fontWight = 400;

  if (titleFont === "title") {
    fontSize = "1.4rem";
    fontWight = "bold";
  } else if (titleFont === "subtitle") {
    fontSize = "0.9375rem";
    fontWight = 600;
  } else if (titleFont === "body") {
    fontSize = "0.875rem";
    fontWight = 400;
  }
  const handleBlur = () => {
    setIsEditing(false);
    onEdit(currentTitle);
  };

  if (readOnly) {
    return (
      <MDTypography
        variant="h6"
        style={{ textAlign: "center", fontSize, fontWight }}
      >
        {displayTitle}
      </MDTypography>
    );
  }

  return isEditing ? (
    <div style={{ display: "flex", flexDirection: "row" }}>
      <input
        type="text"
        value={currentTitle}
        onChange={(e) => setCurrentTitle(e.target.value)}
        onBlur={handleBlur}
        autoFocus
        style={{
          fontSize,
          fontWight,
          textAlign: "center",
          padding: "5px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          width: "80%",
          marginRight: "10px",
        }}
      />
      <MDButton
        size={"10px"}
        color="success"
        variant="contained"
        onClick={() => {
          handleBlur();
          onEdit();
        }}
      >
        <Icon fontSize="medium">check</Icon>
      </MDButton>
    </div>
  ) : (
    <MDTypography
      variant="h6"
      onClick={() => setIsEditing(true)}
      style={{ cursor: "pointer", textAlign: "center", fontSize, fontWight }}
    >
      {displayTitle}
    </MDTypography>
  );
};

export default Titles;
